import "@swisscom/sdx/dist/css/sdx.css";

import React, { useState } from "react";
import { isEmailValid } from "../../helpers/validator";
import Captcha, { useCaptcha } from "./captcha/captcha";
import {
  HTTP_429_TOO_MANY_REQUESTS,
  MIN_LENGTH_OF_OTT,
  REQUEST_FAILED,
  REQUEST_PENDING,
  REQUEST_SUCCESS,
  REQUEST_SUCCESS_BUT_SESSION_VALIDATION_FAILED,
  requestLogin,
  requestOtt,
} from "./backendClientRequests";
import { MY_SERVICE_LINK } from "../../helpers/constants";
import SdxInput from "../../helpers/SdxInputWrapper";

const LOGIN_BUTTON_LABEL = "Signin";

export default function AuthorizationForm({
  email,
  setEmail,
  onAuthorizationComplete,
  onShowSignupForm,
}) {
  const [validEmail, setValidEmail] = useState(isEmailValid(email));
  const [validOttFormat, setValidOttFormat] = useState(false);

  const [requestOttErrorMessage, setRequestOttErrorMessage] =
    useState(undefined);
  const [requestOttStatus, setRequestOttStatus] = useState(undefined);

  const [ott, setOtt] = useState(undefined);

  const [loginStatus, setLoginStatus] = useState(undefined);
  const [loginErrorMessage, setLoginErrorMessage] = useState(undefined);

  return (
    <AuthFormLayout header={"Login"}>
      {
        <div>
          <div
            className="card card--message-info card--icon card--border card--rounded card--clickable"
            aria-labelledby="Information"
          >
            <i
              className="card__item-icon icon icon-046-information-circle icon--s3"
              aria-hidden="true"
            ></i>
            <div className="card__body">
              This form is intended for bulk email senders using an external
              domain. If you are the owner of a Bluewin.ch or Bluemail.ch email
              address and are experiencing issues sending or receiving emails,
              please contact our customer support via
              <a href={MY_SERVICE_LINK}> MyService</a>.
            </div>
          </div>

          <section style={{ paddingTop: "20px" }}>
            <div className="row margin-bottom-2">
              <div className="col">
                Sign in using an email address from the domain for which you
                want to submit a delisting request.
              </div>
            </div>
            <div className="row margin-bottom-2">
              <div className="col">
                <SdxInput
                  id="email-input-field"
                  label={"Mail"}
                  disabled={
                    requestOttStatus === REQUEST_PENDING ||
                    requestOttStatus === REQUEST_SUCCESS
                  }
                  required
                  type="email"
                  value={email}
                  onChange={(val) => {
                    setEmail(val);
                    setRequestOttErrorMessage(undefined);
                  }}
                  validate={(val) => {
                    if (isEmailValid(val)) {
                      setValidEmail(true);
                      return undefined;
                    } else {
                      setValidEmail(false);
                      return "Invalid e-mail address";
                    }
                  }}
                />
              </div>
            </div>

            {requestOttErrorMessage && (
              <div className="row margin-bottom-2">
                <div className="col" style={{ color: "red" }}>
                  {requestOttErrorMessage}
                </div>
              </div>
            )}

            {requestOttStatus === REQUEST_SUCCESS || (
              <RequestLoginOttButton
                hide={requestOttStatus === REQUEST_SUCCESS}
                disable={!validEmail || requestOttStatus === REQUEST_PENDING}
                email={email}
                onClick={() => {
                  setRequestOttStatus(REQUEST_PENDING);
                  setRequestOttErrorMessage(undefined);
                }}
                onOttSent={() => {
                  setRequestOttStatus(REQUEST_SUCCESS);
                }}
                onLoginCreationNeeded={(adminAddressList) => {
                  setRequestOttStatus(REQUEST_FAILED);
                  onShowSignupForm(adminAddressList);
                }}
                onFail={(tooManyRequests) => {
                  setRequestOttStatus(REQUEST_FAILED);
                  setRequestOttErrorMessage(
                    tooManyRequests
                      ? "Too many requests. Please wait for a minute before trying again!"
                      : "Something went wrong. Please verify that the email address is correct and try again.",
                  );
                }}
              />
            )}
          </section>

          <section
            style={{ paddingTop: "20px" }}
            hidden={requestOttStatus != REQUEST_SUCCESS}
          >
            <div className="row margin-bottom-2">
              <div className="col">
                A verification code has been sent to{" "}
                <b>
                  <i>{email}</i>
                </b>
                .
              </div>
              <div className="col">
                Please enter the code in the field below and press{" "}
                {LOGIN_BUTTON_LABEL} to enter the delisting form.
              </div>
            </div>
            <div className="row margin-bottom-2">
              <div className="col" style={{ maxWidth: 300 }}>
                <SdxInput
                  id="ott-input-field"
                  label={"Verification code"}
                  disabled={
                    loginStatus === REQUEST_PENDING ||
                    loginStatus === REQUEST_SUCCESS ||
                    loginStatus === REQUEST_FAILED
                  }
                  required
                  type="text"
                  value={ott}
                  onChange={(val) => {
                    const ottTrim = val.trim();
                    setOtt(ottTrim);
                    setLoginErrorMessage(undefined);
                  }}
                  validate={(val) => {
                    const ottTrim = val.trim();
                    if (ottTrim.length < MIN_LENGTH_OF_OTT) {
                      setValidOttFormat(false);
                      return "Invalid format";
                    }
                    setValidOttFormat(true);
                    return undefined;
                  }}
                />
              </div>
            </div>

            {loginErrorMessage && (
              <div className="row margin-bottom-2">
                <div className="col" style={{ color: "red" }}>
                  {loginErrorMessage}
                </div>
              </div>
            )}

            <LoginButton
              ott={ott}
              email={email}
              hasSessionValidationIssue={
                loginStatus == REQUEST_SUCCESS_BUT_SESSION_VALIDATION_FAILED
              }
              onLoginClick={() => {
                setLoginStatus(REQUEST_PENDING);
                setLoginErrorMessage(undefined);
              }}
              onSuccess={() => {
                onAuthorizationComplete(email).then((signedInAs) => {
                  if (signedInAs != null) {
                    setLoginStatus(REQUEST_SUCCESS);
                  } else {
                    setLoginStatus(
                      REQUEST_SUCCESS_BUT_SESSION_VALIDATION_FAILED,
                    );
                  }
                });
                setLoginStatus(REQUEST_SUCCESS);
              }}
              onFail={() => {
                setLoginStatus(REQUEST_FAILED);
                setLoginErrorMessage("Failed to login.");
              }}
              onBack={() => {
                setLoginStatus(undefined);
                setValidOttFormat(undefined);
                setRequestOttErrorMessage(undefined);
                setRequestOttStatus(undefined);
                setOtt(undefined);
                setLoginStatus(undefined);
                setLoginErrorMessage(undefined);
              }}
              hide={requestOttStatus === REQUEST_SUCCESS && !validEmail}
              disable={
                !ott || !validOttFormat || ott.length < MIN_LENGTH_OF_OTT
              }
            />
          </section>
        </div>
      }
    </AuthFormLayout>
  );
}

function LoginButton({
  disable,
  hide,
  hasSessionValidationIssue,
  ott,
  email,
  onLoginClick,
  onSuccess,
  onFail,
  onBack,
}) {
  const [showAsPending, setShowAsPending] = useState(false);
  const [showRestartButton, setShowRestartButton] = useState(false);

  return (
    !hide && (
      <div style={{ paddingTop: "20px" }}>
        <sdx-button-group layout="fixed">
          {!showRestartButton ? (
            <sdx-button
              id="loginButton"
              label={hasSessionValidationIssue ? "Retry" : LOGIN_BUTTON_LABEL}
              onClick={() => {
                onLoginClick();
                setShowAsPending(true);
                if (hasSessionValidationIssue) {
                  onSuccess();
                } else {
                  requestLogin(email, ott).then((success) => {
                    if (success) {
                      onSuccess();
                    } else {
                      onFail();
                      setShowAsPending(false);
                      setShowRestartButton(true);
                    }
                  });
                }
              }}
              loading={showAsPending && !hasSessionValidationIssue}
              disabled={disable}
            ></sdx-button>
          ) : (
            <sdx-button
              id="restartLoginButton"
              label={"Back"}
              onClick={() => {
                setShowRestartButton(false);
                onBack();
              }}
            ></sdx-button>
          )}
        </sdx-button-group>
      </div>
    )
  );
}

function RequestLoginOttButton({
  hide,
  disable,
  email,
  onClick,
  onOttSent,
  onLoginCreationNeeded,
  onFail,
}) {
  const [showAsPending, setShowAsPending] = useState(false);
  const { validateCaptchaCompletion, getCaptchaResponse, showCaptchaError } =
    useCaptcha();

  return (
    !hide && (
      <div style={{ paddingTop: "20px" }}>
        <Captcha showErrorMessage={showCaptchaError} />
        <sdx-button-group layout="fixed">
          <sdx-button
            id="requestLoginOttButton"
            label={"Next"}
            onClick={() => {
              if (validateCaptchaCompletion()) {
                onClick();
                setShowAsPending(true);
                requestOtt(email, getCaptchaResponse())
                  .then((response) => {
                    setShowAsPending(false);
                    if (response.sentOtt) {
                      onOttSent();
                      return;
                    } else {
                      if (
                        !response.isRegistered &&
                        response.requiresAdminAuth
                      ) {
                        onLoginCreationNeeded(response.adminAddressList);
                        return;
                      }
                    }
                    console.error(
                      "Unexpected response from OTT request",
                      response,
                    );
                  })
                  .catch((e) => {
                    setShowAsPending(false);
                    const tooManyRequests =
                      e.status === HTTP_429_TOO_MANY_REQUESTS;
                    onFail(tooManyRequests);
                  });
              }
            }}
            disabled={disable}
            loading={showAsPending}
          ></sdx-button>
        </sdx-button-group>
      </div>
    )
  );
}

export function AuthFormLayout({ header, children }) {
  return (
    <div className="AuthorizationForm">
      <div className="container">
        <div className="margin-v-4">
          <h1 className="text-h1 sc-navy margin-v-4">{header}</h1>
          <div className="row">
            <div className="col-xs-12 col-md-6">{children}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
